<!--
Copyright 2020 ODK Central Developers
See the NOTICE file at the top-level directory of this distribution and at
https://github.com/getodk/central-frontend/blob/master/NOTICE.

This file is part of ODK Central. It is subject to the license terms in
the LICENSE file found in the top-level directory of this distribution and at
https://www.apache.org/licenses/LICENSE-2.0. No part of ODK Central,
including this file, may be copied, modified, propagated, or distributed
except according to the terms contained in the LICENSE file.
-->
<template>
  <tr :class="htmlClass">
    <td class="display-name">
      <span :title="publicLink.displayName">{{ publicLink.displayName }}</span>
    </td>
    <td class="once">
      {{ publicLink.once ? $t('common.yes') : $t('common.no') }}
    </td>
    <td class="access-link">
      <template v-if="publicLink.token == null">{{ $t('revoked') }}</template>
      <template v-else-if="form != null">
        <selectable v-if="enketoId != null">{{ url }}</selectable>
        <span v-else class="unavailable" :title="$t('unavailable.title')">
          <span>{{ $t('unavailable.text') }}</span>
          <span class="icon-question-circle"></span>
        </span>
      </template>
    </td>
    <td>
      <button v-if="publicLink.token != null" type="button"
        class="btn btn-danger" @click="$emit('revoke', publicLink)">
        <span class="icon-times-circle"></span>{{ $t('action.revoke') }}&hellip;
      </button>
    </td>
  </tr>
</template>

<script>
import Selectable from '../selectable.vue';
import { enketoBasePath } from '../../util/util';
import { requestData } from '../../store/modules/request';

export default {
  name: 'PublicLinkRow',
  components: { Selectable },
  props: {
    publicLink: {
      type: Object,
      required: true
    },
    highlighted: Number // eslint-disable-line vue/require-default-prop
  },
  computed: {
    // The component does not assume that this data will exist when the
    // component is created.
    ...requestData(['form']),
    htmlClass() {
      return {
        'public-link-row': true,
        success: this.publicLink.id === this.highlighted
      };
    },
    enketoId() {
      return this.publicLink.once ? this.form.enketoOnceId : this.form.enketoId;
    },
    url() {
      const encodedId = encodeURIComponent(this.enketoId);
      return `${window.location.origin}${enketoBasePath}/single/${encodedId}?st=${this.publicLink.token}`;
    }
  }
};
</script>

<style lang="scss">
.public-link-row {
  .table tbody & td { vertical-align: middle; }

  .display-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .unavailable { cursor: help; }
  .icon-question-circle { margin-left: 5px; }
}
</style>

<i18n lang="json5">
{
  "en": {
    "action": {
      "revoke": "Revoke"
    },
    // This text is shown for a Public Access Link that has been revoked. The
    // text appears on its own and is not part of a longer sentence.
    "revoked": "Revoked",
    "unavailable": {
      // This text is shown for a Public Access Link that is not available yet.
      "text": "Not available yet",
      "title": "Public Access Link is not available yet. It has not finished being processed. Please refresh later and try again."
    }
  }
}
</i18n>

<!-- Autogenerated by destructure.js -->
<i18n>
{
  "cs": {
    "action": {
      "revoke": "Odebrat"
    },
    "revoked": "Odebrán",
    "unavailable": {
      "text": "Už není dostupný",
      "title": "Veřejně přístupný odkaz už není k dispozici. Zpracování se nedokončilo. Aktualizujte prosím později a zkuste to znovu."
    }
  },
  "de": {
    "action": {
      "revoke": "Widerrufen"
    },
    "revoked": "Widerrufen",
    "unavailable": {
      "text": "Noch nicht verfügbar",
      "title": "Öffentlicher Zugangslink noch nicht verfügbar. Er wurde noch nicht vollständig verarbeitet. Bitte aktualisieren Sie später und versuchen Sie es erneut."
    }
  },
  "es": {
    "action": {
      "revoke": "Revocar"
    },
    "revoked": "Revocar",
    "unavailable": {
      "text": "Todavía no disponible",
      "title": "El enlace de acceso público aún no está disponible. No se ha terminado de procesar. Por favor actualice más tarde y vuelva a intentarlo."
    }
  },
  "fr": {
    "action": {
      "revoke": "Révoquer"
    },
    "revoked": "Révoqué",
    "unavailable": {
      "text": "Pas encore disponible",
      "title": "Le lien d'accès public n'est pas encore disponible. Il n'a pas encore été traité. Veuillez rafraîchir plus tard et réessayer."
    }
  }
}
</i18n>
